import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { NotificationType } from '../../../modules/room/room-banner/room-banner.component';

export interface Notification {
    id: string;
    text: string;
    type: NotificationType;
    btnText?: string;
    secondaryBtnText?: string;
    autoDismiss?: boolean;
    dismissTimeout?: number;
    showCloseButton?: boolean;
    handRaised?: boolean;
    participantId?: string;
    notificationType?: string;
}

// Interface for chat message objects
export interface ChatMessage {
    userName?: string;
    message?: string;
    type?: string;
    [key: string]: any;
}

export interface HandRaisedTimeoutEvent {
    participantId: string;
    shouldLowerHand: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService {
    // BehaviorSubject to track active notifications
    private notificationsSubject = new BehaviorSubject<Notification[]>([]);
    notifications$ = this.notificationsSubject.asObservable();

    // Event for when a hand raised notification times out
    handRaisedTimedOut = new Subject<HandRaisedTimeoutEvent>();

    // Backward compatibility for chat functionality
    messagesUpdated: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    chatMessagesUpdated: BehaviorSubject<number> = new BehaviorSubject<number>(
        0,
    );
    dmMessagesUpdated: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    insightsMessagesUpdated: BehaviorSubject<number> =
        new BehaviorSubject<number>(0);

    // Arrays to store unread messages for backward compatibility
    unreadChatMessages: any[] = [];
    unreadDmMessages: any[] = [];
    unreadInsightsMessages: any[] = [];

    // Active hand raised notification
    private activeHandRaisedNotification: string = '';

    // Map to track which participants have chosen to keep their hand raised
    private keepHandRaisedMap: Map<string, boolean> = new Map();

    constructor() {}

    /**
     * Checks if a participant has chosen to keep their hand raised
     * @param participantId The participant ID to check
     * @returns True if the participant should keep their hand raised
     */
    shouldKeepHandRaised(participantId: string): boolean {
        return this.keepHandRaisedMap.get(participantId) || false;
    }

    /**
     * Marks a participant as wanting to keep their hand raised
     * @param participantId The participant ID to mark
     */
    setKeepHandRaised(participantId: string, keepRaised: boolean = true): void {
        this.keepHandRaisedMap.set(participantId, keepRaised);
    }

    /**
     * Show a notification
     * @param text The text to display in the notification
     * @param type The type of notification (info, success, warning, error)
     * @param btnText Optional button text (defaults to 'Close')
     * @param autoDismiss Whether to auto dismiss the notification
     * @param dismissTimeout Time in ms before auto dismissing (default 5000ms)
     * @param showCloseButton Whether to show the close button
     * @param handRaised Whether the notification is for a hand raised event
     * @param participantId The ID of the participant involved in the hand raised event
     * @param secondaryBtnText Optional secondary button text
     * @param notificationType Optional identifier for the notification type (for cooldown tracking)
     * @returns The ID of the created notification
     */
    show(
        text: string,
        type: NotificationType = 'info',
        btnText: string = 'Close',
        autoDismiss: boolean = false,
        dismissTimeout: number = 5000,
        showCloseButton: boolean = true,
        handRaised: boolean = false,
        participantId?: string,
        secondaryBtnText?: string,
        notificationType?: string,
    ): string {
        // Check if this notification is in cooldown
        if (
            notificationType &&
            this.isNotificationInCooldown(notificationType)
        ) {
            return '';
        }

        // Check for duplicate notifications with the same content
        const currentNotifications = this.notificationsSubject.value;
        const duplicateNotification = currentNotifications.find(
            (notification) =>
                notification.text === text &&
                notification.type === type &&
                notification.btnText === btnText &&
                notification.secondaryBtnText === secondaryBtnText,
        );

        // If a duplicate notification exists, return its ID instead of creating a new one
        if (duplicateNotification) {
            return duplicateNotification.id;
        }

        const id = this.generateId();
        const notification: Notification = {
            id,
            text,
            type,
            btnText,
            secondaryBtnText,
            autoDismiss,
            dismissTimeout,
            showCloseButton,
            handRaised,
            participantId,
            notificationType,
        };

        this.notificationsSubject.next([...currentNotifications, notification]);

        if (autoDismiss) {
            setTimeout(() => {
                // Important: when auto-dismissed, skipAction should be false to allow hand lowering
                this.dismiss(id, false, 'auto');
            }, dismissTimeout);
        }

        return id;
    }

    /**
     * Show an info notification
     * @param text The text to display
     * @param btnText Optional button text
     * @param autoDismiss Whether to auto dismiss
     * @returns The notification ID
     */
    info(
        text: string,
        btnText: string = 'Close',
        autoDismiss: boolean = false,
    ): string {
        return this.show(text, 'info', btnText, autoDismiss);
    }

    /**
     * Show a success notification
     * @param text The text to display
     * @param btnText Optional button text
     * @param autoDismiss Whether to auto dismiss
     * @returns The notification ID
     */
    success(
        text: string,
        btnText: string = 'Close',
        autoDismiss: boolean = true,
    ): string {
        return this.show(text, 'success', btnText, autoDismiss);
    }

    /**
     * Show a warning notification
     * @param text The text to display
     * @param btnText Optional button text
     * @param autoDismiss Whether to auto dismiss
     * @returns The notification ID
     */
    warning(
        text: string,
        btnText: string = 'Close',
        autoDismiss: boolean = false,
    ): string {
        return this.show(text, 'warning', btnText, autoDismiss);
    }

    /**
     * Show an error notification
     * @param text The text to display
     * @param btnText Optional button text
     * @param autoDismiss Whether to auto dismiss
     * @returns The notification ID
     */
    error(
        text: string,
        btnText: string = 'Close',
        autoDismiss: boolean = false,
    ): string {
        return this.show(text, 'error', btnText, autoDismiss);
    }

    /**
     * Show a hand raised notification
     * @param participantName The name of the participant who raised their hand
     * @param participantId The ID of the participant who raised their hand
     * @param isOwnHand Whether it's the local participant's hand
     * @param btnText Optional button text for keeping hand raised
     * @param autoDismiss Whether to auto dismiss (default true with 10s timeout for own hand)
     * @returns The notification ID or empty string if notification wasn't shown
     */
    handRaised(
        participantName: string,
        participantId: string,
        isOwnHand: boolean = false,
        btnText: string = 'Keep raised',
        autoDismiss: boolean = isOwnHand,
    ): string {
        // Handle notification for the local participant's own hand
        if (isOwnHand) {
            // Reset the keep hand raised flag when a new hand raised notification is created
            this.keepHandRaisedMap.set(participantId, false);

            // Dismiss any existing hand raised notification for this participant
            this.dismissHandRaisedForParticipant(participantId);

            const text = `You raised your hand`;

            // For hand raised notifications, we'll use the component's own autoDismiss functionality
            // This way the progress bar and dismissal are perfectly synchronized
            const id = this.show(
                text,
                'info',
                btnText,
                autoDismiss,
                10000, // 10 seconds timeout for auto-dismiss
                isOwnHand, // Only show close button for own hand
                true, // This is a hand raised notification
                participantId,
            );

            this.activeHandRaisedNotification = id;
            return id;
        }
        // Handle notification for other participants' hands
        else {
            // Notification for other participants when someone raises their hand
            const text = `${participantName} raised their hand`;

            // For other participants' hand notifications:
            // - Use 5 second timeout
            // - Don't show progress bar (handRaised flag set to false)
            // - Auto-dismiss is true
            const id = this.show(
                text,
                'info',
                'Dismiss',
                true, // Auto-dismiss
                5000, // 5 seconds timeout
                true, // Show close button
                false, // Not a hand raised notification that shows progress bar
                participantId,
                undefined, // No secondary button
                'hand-raised-notification', // Add notification type for tracking
            );

            return id;
        }
    }

    /**
     * Lower hand notification for a participant
     * @param participantId The ID of the participant whose hand to lower
     */
    lowerHand(participantId: string): void {
        if (!participantId) return;

        // Reset the keep hand raised flag when hand is manually lowered
        if (this.keepHandRaisedMap.has(participantId)) {
            this.keepHandRaisedMap.set(participantId, false);
        }

        // Dismiss any existing hand raised notification for this participant
        // Pass skipEvent=true to avoid emitting events that would cause a circular reference
        this.dismissHandRaisedForParticipant(participantId, true);
    }

    /**
     * Dismiss hand raised notifications for a specific participant
     * @param participantId The ID of the participant
     * @param skipEvent Whether to skip emitting the hand raised timeout event
     */
    private dismissHandRaisedForParticipant(
        participantId: string,
        skipEvent: boolean = false,
    ): void {
        if (!participantId) return;

        const currentNotifications = this.notificationsSubject.value;
        const handNotifications = currentNotifications.filter(
            (notification) =>
                notification.handRaised &&
                notification.participantId === participantId,
        );

        // If skipEvent is true, we are being called from lowerHand and we want to
        // avoid the circular reference of emitting the event
        if (skipEvent) {
            // Instead of calling dismiss (which would emit events), directly remove
            // the notifications from the subject
            if (handNotifications.length > 0) {
                const notificationIds = handNotifications.map((n) => n.id);
                const updatedNotifications = currentNotifications.filter(
                    (notification) =>
                        !notificationIds.includes(notification.id),
                );
                this.notificationsSubject.next(updatedNotifications);
            }
        } else {
            // Normal flow - but pass skipAction=true to prevent hand lowering logic
            handNotifications.forEach((notification) => {
                this.dismiss(notification.id, true);
            });
        }

        if (this.activeHandRaisedNotification) {
            this.activeHandRaisedNotification = '';
        }
    }

    /**
     * Dismisses a notification by its ID
     * @param id The notification ID
     * @param skipAction Whether to skip the auto-action (e.g., lowering hand)
     * @param dismissedBy The action that caused the dismissal ('cancel', 'troubleshoot', 'auto', 'close')
     */
    dismiss(
        id: string,
        skipAction: boolean = false,
        dismissedBy: 'cancel' | 'troubleshoot' | 'auto' | 'close' = 'close',
    ): void {
        const notifications = this.notificationsSubject.getValue();
        const notificationIndex = notifications.findIndex((n) => n.id === id);

        if (notificationIndex === -1) {
            return;
        }

        const notification = notifications[notificationIndex];

        // If this is a hand raised notification, we need to handle it specially
        if (notification.handRaised && notification.participantId) {
            // If the notification is auto-dismissed (via timeout) and the user hasn't explicitly chosen
            // to keep their hand raised, we should lower it

            // Important: For auto-dismissed notifications, skipAction should be false
            // If skipAction is true, it means the user clicked "Keep it raised"
            const keepHandRaised = this.shouldKeepHandRaised(
                notification.participantId,
            );
            const shouldLowerHand = !skipAction && !keepHandRaised;

            // Emit an event so the RoomComponent can handle the timeout
            this.handRaisedTimedOut.next({
                participantId: notification.participantId,
                shouldLowerHand: shouldLowerHand,
            });
        }

        // Track dismissed notification for cooldown if it has a notificationType
        if (notification.notificationType) {
            this.setNotificationCooldown(notification.notificationType);
        }

        // Remove the notification from our list
        const updatedNotifications = [
            ...notifications.slice(0, notificationIndex),
            ...notifications.slice(notificationIndex + 1),
        ];

        this.notificationsSubject.next(updatedNotifications);

        // If we just dismissed the active hand raised notification, clear the reference
        if (id === this.activeHandRaisedNotification) {
            this.activeHandRaisedNotification = '';
        }
    }

    /**
     * Dismiss all notifications
     */
    dismissAll(): void {
        this.notificationsSubject.next([]);
        this.activeHandRaisedNotification = '';
    }

    /**
     * Generate a unique ID for a notification
     * @returns A unique string ID
     */
    private generateId(): string {
        return (
            'notification-' +
            new Date().getTime() +
            '-' +
            Math.floor(Math.random() * 1000)
        );
    }

    // Backward compatibility methods for the chat system
    updateMessagesCount() {
        this.messagesUpdated.next(
            this.unreadChatMessages.length +
                this.unreadDmMessages.length +
                this.unreadInsightsMessages.length,
        );
    }

    addChatMessage(message: any) {
        this.unreadChatMessages.push(message);
        this.chatMessagesUpdated.next(this.unreadChatMessages.length);
        this.updateMessagesCount();

        // No longer show notification banners for chat messages
        // The chat messages will still be tracked for the badge count
    }

    addDmMessage(message: any) {
        this.unreadDmMessages.push(message);
        this.dmMessagesUpdated.next(this.unreadDmMessages.length);
        this.updateMessagesCount();

        // No longer show notification banners for direct messages
        // The DM messages will still be tracked for the badge count
    }

    addInsightsMessage(message: any) {
        this.unreadInsightsMessages.push(message);
        this.insightsMessagesUpdated.next(this.unreadInsightsMessages.length);
        this.updateMessagesCount();

        // No longer show notification banners for insights messages
        // The insights messages will still be tracked for the badge count
    }

    clearMessages(type: 'chat' | 'dm' | 'insights') {
        if (type === 'chat' && this.unreadChatMessages.length) {
            this.unreadChatMessages = [];
            this.chatMessagesUpdated.next(0);
        } else if (type === 'dm' && this.unreadDmMessages.length) {
            this.unreadDmMessages = [];
            this.dmMessagesUpdated.next(0);
        } else if (type === 'insights' && this.unreadInsightsMessages.length) {
            this.unreadInsightsMessages = [];
            this.insightsMessagesUpdated.next(0);
        }
        this.updateMessagesCount();
    }

    clearChatMessages() {
        this.clearMessages('chat');
    }

    clearDmMessages() {
        this.clearMessages('dm');
    }

    clearInsightsMessages() {
        this.clearMessages('insights');
    }

    /**
     * Sets a cooldown for a notification type
     * @param notificationType The notification type to set cooldown for
     */
    private setNotificationCooldown(notificationType: string): void {
        // Define cooldown periods for different notification types (in milliseconds)
        const cooldownPeriods: { [key: string]: number } = {
            'network-quality': 180000, // 3 minutes for network quality notifications
            // Add other notification types and their cooldown periods here
        };

        const cooldownPeriod = cooldownPeriods[notificationType] || 0;

        if (cooldownPeriod > 0) {
            const cooldownKey = `notification-cooldown-${notificationType}`;
            const cooldownEndTime = Date.now() + cooldownPeriod;
            localStorage.setItem(cooldownKey, cooldownEndTime.toString());
        }
    }

    /**
     * Checks if a notification is in cooldown
     * @param notificationType The notification type to check
     * @returns True if the notification is in cooldown and should not be shown
     */
    private isNotificationInCooldown(notificationType: string): boolean {
        const cooldownKey = `notification-cooldown-${notificationType}`;
        const cooldownEndTimeStr = localStorage.getItem(cooldownKey);

        if (!cooldownEndTimeStr) {
            return false;
        }

        const cooldownEndTime = parseInt(cooldownEndTimeStr, 10);
        const now = Date.now();

        // If the cooldown period has passed, remove the cooldown from localStorage
        if (now >= cooldownEndTime) {
            localStorage.removeItem(cooldownKey);
            return false;
        }

        return true;
    }

    /**
     * Clears all notification cooldowns
     * This should be called when a user leaves the room
     */
    clearNotificationCooldowns(): void {
        // Get all keys from localStorage that start with 'notification-cooldown-'
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('notification-cooldown-')) {
                keysToRemove.push(key);
            }
        }

        // Remove all cooldown keys
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
}
